import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import type { NostrEvent } from "@/nostr/types"
import { useProfiles } from "@/hooks/useProfiles"
import { reactionEmoji } from "@/nostr/api/interactions"
import { Avatar } from "./Avatar"
import { Icon } from "./Icon"
import { Timestamp } from "./Timestamp"
import { profilePath } from "./UserName"
import { formatCount } from "@/utils/format"
import { cn } from "./helpers"

export type InteractionTab = "replies" | "reposts" | "reactions" | "quotes"

export interface InteractionBarProps {
  replies: number
  reposts: number
  reactions: number
  quotes: number
  active: InteractionTab
  onChange: (tab: InteractionTab) => void
}

/**
 * The summary bar under a note: replies, reposts, reactions and quotes. Every
 * counter is also the tab selector for the detail view below it.
 */
export function InteractionBar({ replies, reposts, reactions, quotes, active, onChange }: InteractionBarProps) {
  const items: Array<{ tab: InteractionTab; icon: string; count: number; label: string }> = [
    { tab: "replies", icon: "reply", count: replies, label: "Replies" },
    { tab: "reposts", icon: "repeat", count: reposts, label: "Reposts" },
    { tab: "reactions", icon: "heart", count: reactions, label: "Reactions" },
    { tab: "quotes", icon: "document", count: quotes, label: "Quotes" },
  ]

  return (
    <div className="interaction-bar" role="tablist" aria-label="Note interactions">
      {items.map((item) => (
        <button
          key={item.tab}
          type="button"
          role="tab"
          aria-selected={active === item.tab}
          className={cn("interaction-stat", active === item.tab && "active")}
          onClick={() => onChange(item.tab)}
        >
          <Icon name={item.icon} size={16} />
          <span>{formatCount(item.count)}</span>
          <span className="interaction-label">{item.label}</span>
        </button>
      ))}
    </div>
  )
}

export interface ActorListProps {
  pubkeys: string[]
  empty: ReactNode
  /** Optional emoji per pubkey (reaction tab). */
  emojiFor?: Map<string, string>
  /** Optional timestamp per pubkey. */
  timeFor?: Map<string, number>
}

/** One row per person: avatar, name, handle and optional emoji/timestamp. */
export function ActorList({ pubkeys, empty, emojiFor, timeFor }: ActorListProps) {
  const profiles = useProfiles(pubkeys)
  if (pubkeys.length === 0) return <p className="muted small">{empty}</p>

  return (
    <ul className="actor-list">
      {pubkeys.map((pubkey) => {
        const profile = profiles.get(pubkey)
        const emoji = emojiFor?.get(pubkey)
        const time = timeFor?.get(pubkey)
        return (
          <li key={pubkey}>
            <Link to={profilePath(pubkey)} className="actor-row">
              <Avatar src={profile?.picture} label={profile?.displayName || pubkey} size={32} />
              <span className="actor-row-meta">
                <span className="actor-row-name">
                  {profile?.displayName || profile?.name || `${pubkey.slice(0, 10)}…`}
                </span>
                <span className="muted small">{profile?.nip05 || `${pubkey.slice(0, 12)}…`}</span>
              </span>
              {emoji && <span className="reaction-emoji" aria-label={`reacted with ${emoji}`}>{emoji}</span>}
              {typeof time === "number" && <Timestamp seconds={time} className="muted small" />}
            </Link>
          </li>
        )
      })}
    </ul>
  )
}

export interface ReactionSummaryProps {
  groups: Array<{ emoji: string; events: NostrEvent[] }>
}

/** Emoji tally chips (e.g. ❤️ 8  🔥 3) shown above the reactor list. */
export function ReactionSummary({ groups }: ReactionSummaryProps) {
  if (groups.length === 0) return null
  return (
    <div className="reaction-summary">
      {groups.map((group) => (
        <span key={group.emoji} className="reaction-chip">
          <span className="reaction-emoji">{group.emoji}</span>
          <span className="reaction-count">{formatCount(group.events.length)}</span>
        </span>
      ))}
    </div>
  )
}

/** Maps an emoji per author (newest reaction wins) for the reaction list. */
export function reactionEmojiMap(events: NostrEvent[]): Map<string, string> {
  const newest = new Map<string, NostrEvent>()
  for (const event of events) {
    const current = newest.get(event.pubkey)
    if (!current || current.created_at < event.created_at) newest.set(event.pubkey, event)
  }
  const map = new Map<string, string>()
  for (const [pubkey, event] of newest) map.set(pubkey, reactionEmoji(event))
  return map
}

/** Maps the newest interaction timestamp per author. */
export function interactionTimeMap(events: NostrEvent[]): Map<string, number> {
  const map = new Map<string, number>()
  for (const event of events) {
    const current = map.get(event.pubkey)
    if (!current || current < event.created_at) map.set(event.pubkey, event.created_at)
  }
  return map
}
